import { useCallback, useMemo, useState, type JSX } from 'react';
import type { CSSProperties } from 'react';
import { useIsomorphicLayoutEffect } from 'ahooks';
import type { CarouselNavigationItemProps } from '@archipro-design/aria';
import { pxToRem, useTheme } from '@archipro-design/aria';
import { Button, CarouselNavigation, useStyles } from '@archipro-design/aria';

import * as S from './ProductGalleryThumbs.style';
import { ArrowLeftSLine, ArrowRightSLine } from '@archipro-design/icons';
import galleryThumbTransition from '../../util/gallery-thumb-transition';
import {
    getThumbGroupCount,
    resolveThumbGroupForSlide,
} from '../../util/product-gallery-thumb-groups';

import { PRODUCT_THUMB_GAP, PRODUCT_THUMB_SIZE } from './ProductGallery.style';

interface ProductGalleryProps {
    navigation: {
        id: number;
        thumbnail: JSX.Element;
    }[];
    activeGallerySlide: number;
    swipeInitialised: boolean;
    slideToIndex: (index: number, speed: number) => void;
}

const ProductGalleryThumbs = ({
    navigation,
    activeGallerySlide,
    swipeInitialised,
    slideToIndex,
}: ProductGalleryProps) => {
    const [currentTabGroup, setCurrentTabGroup] = useState(1);

    const THUMB_WIDTH = PRODUCT_THUMB_SIZE + PRODUCT_THUMB_GAP;
    const slideCount = navigation.length;
    const thumbGroups = useMemo(
        () => getThumbGroupCount(slideCount),
        [slideCount],
    );
    const totalTabGroups = thumbGroups.length;

    const showPrevBtn = currentTabGroup > 1;
    const showNextBtn = currentTabGroup !== totalTabGroups && slideCount > 6;

    /** Moves the visible thumb group, keeping it within the available groups. */
    const moveTabGroup = useCallback(
        (delta: number) =>
            setCurrentTabGroup((prev) => {
                const next = Math.min(
                    Math.max(prev + delta, 1),
                    Math.max(totalTabGroups, 1),
                );
                return prev === next ? prev : next;
            }),
        [totalTabGroups],
    );

    /**
     * Parent gallery owns the focused slide index. Only move the visible thumb
     * group when the active slide leaves the current group — never mirror the
     * index into local state (that fought Swiper and threw React #185).
     *
     * When the gallery shrinks to a single useful group (≤6 slides), reset the
     * group so a prior multi-group browse cannot leave a stale group index.
     */
    useIsomorphicLayoutEffect(() => {
        if (slideCount <= 6 || totalTabGroups <= 1) {
            setCurrentTabGroup((prev) => (prev === 1 ? prev : 1));
            return;
        }

        const nextGroup = resolveThumbGroupForSlide(
            thumbGroups,
            activeGallerySlide,
        );
        if (nextGroup === null) {
            moveTabGroup(0);
            return;
        }

        setCurrentTabGroup((prev) => (prev === nextGroup ? prev : nextGroup));
    }, [
        activeGallerySlide,
        slideCount,
        thumbGroups,
        totalTabGroups,
        moveTabGroup,
    ]);

    const translateWidth = galleryThumbTransition(
        THUMB_WIDTH,
        slideCount,
        currentTabGroup,
        totalTabGroups,
        thumbGroups,
    );

    const { css } = useStyles({
        THUMB_WIDTH,
        slideCount,
        showPrevBtn,
        showNextBtn,
        translateWidth,
        swipeInitialised,
    });
    const { siteVariables } = useTheme();

    const navigationItems = useMemo(
        () => ({
            'aria-label': 'Product gallery navigation',
            items: navigation.map((item, index) => ({
                key: `thumbnail-slide-${item.id}`,
                'aria-controls': item.id,
                style: {
                    '--thumb-ring-color':
                        index === activeGallerySlide
                            ? siteVariables.colors.charcoal[250]
                            : 'transparent',
                } as CSSProperties,
                content: item.thumbnail,
            })),
        }),
        [activeGallerySlide, navigation, siteVariables.colors.charcoal],
    );

    return (
        <div className={css(S.ThumbsContainer)}>
            <Button
                className={css(S.LeftNavBtn)}
                transparent
                onClick={() => moveTabGroup(-1)}
                icon={<ArrowLeftSLine />}
            />

            <div className={css(S.ThumbList)}>
                {CarouselNavigation.create(navigationItems, {
                    generateKey: true,
                    defaultProps: () => ({
                        className: css(S.ThumbSlider),
                        maxTabs: 5,
                        activeTabIndex: activeGallerySlide,
                        design: {
                            width: pxToRem(slideCount * THUMB_WIDTH),
                        },
                    }),
                    overrideProps: () => ({
                        onItemClick: (
                            _,
                            itemProps: CarouselNavigationItemProps,
                        ) => {
                            const { index } = itemProps;
                            if (typeof index === 'number') {
                                slideToIndex(index, 400);
                            }
                        },
                    }),
                })}
            </div>
            <Button
                className={css(S.RightNavBtn)}
                transparent
                onClick={() => moveTabGroup(1)}
                icon={<ArrowRightSLine />}
            />
        </div>
    );
};

export default ProductGalleryThumbs;
